import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import styles from './Select.scss';

const Select = ({
					name,
					options,
					onChange,
					selected,
					placeholder = 'Select...',
					isDisabled,
					isGrouped,
					error = '',
					layout = 'Default',
					color = '',
					id,
				}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedValue, setSelectedValue] = useState(null);
	const selectRef = useRef(null);
	const menuRef = useRef(null);

	// Find and set the initial selected value
	useEffect(() => {
		let initialSelected = null;
		if (selected) {
			if (isGrouped) {
				for (const group of options) {
					const found = group.options.find(opt => opt.value === selected);
					if (found) {
						initialSelected = found;
						break;
					}
				}
			} else {
				initialSelected = options.find(opt => opt.value === selected);
			}
		}
		setSelectedValue(initialSelected);
	}, [selected, options, isGrouped]);

	// Position and size the menu to fit within its parent row and modal viewport
	useEffect(() => {
		if (isOpen && menuRef.current && selectRef.current) {
			const selectRect = selectRef.current.getBoundingClientRect();
			const menu = menuRef.current;

			const modalContainer = selectRef.current.closest('[data-modal-container]');
			let modalPaddingRight = 0;
			let modalPaddingBottom = 10; // Fallback bottom padding

			if (modalContainer) {
				const modalStyles = window.getComputedStyle(modalContainer);
				modalPaddingRight = parseFloat(modalStyles.paddingRight) || 0;
				modalPaddingBottom = parseFloat(modalStyles.paddingBottom) || 10;
			}

			// --- Horizontal constraint (within the row, respecting modal padding) ---
			const rowContainer = selectRef.current.closest('[data-row-container]');
			let rowRect;

			if (rowContainer) {
				rowRect = rowContainer.getBoundingClientRect();
			} else {
				rowRect = {left: 0, right: window.innerWidth}; // Fallback
			}
			const spaceRight = rowRect.right - selectRect.left - modalPaddingRight;
			menu.style.minWidth = `${selectRect.width}px`;
			menu.style.maxWidth = `${spaceRight}px`;

			// --- Vertical constraint (within the modal, or the viewport as fallback) ---
			const bottomBoundary = modalContainer
			  ? modalContainer.getBoundingClientRect().bottom - modalPaddingBottom
			  : window.innerHeight - modalPaddingBottom;
			menu.style.maxHeight = `${bottomBoundary - selectRect.bottom}px`;
		}
	}, [isOpen]);

	const handleOptionClick = (option) => {
		setSelectedValue(option);
		setIsOpen(false);
		if (onChange) {
			onChange({value: option.value, label: option.label});
		}
	};
	const toggleDropdown = () => {
		if (!isDisabled) {
			setIsOpen(!isOpen);
		}
	};

	const handleBlur = (e) => {
		// Close dropdown if the focus moves outside the component
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsOpen(false);
		}
	};

	// --- Class Names ---
	let labelClassInner = styles[layout] || styles.Default;
	if (isDisabled) labelClassInner += ` ${styles.disabled}`;
	if (color) labelClassInner += ` ${styles[color]}`;
	if (isOpen) labelClassInner += ` ${styles.isOpen}`;

	return (
	  <div
		className={styles.container}
		ref={selectRef}
		id={id}
		onBlur={handleBlur}>
		  <label className={labelClassInner}>
			  <div
				className={styles.control}
				onClick={toggleDropdown}
				role="button"
				tabIndex={isDisabled ? -1 : 0}
				onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleDropdown()}
			  >
				  <span className={selectedValue ? styles.value : styles.placeholder}>
					{selectedValue ? (selectedValue.labelShort || (selectedValue.code ? `+${selectedValue.code}` : selectedValue.label)) : placeholder}
				  </span>
				  <span className={styles.indicator}/>
			  </div>
			  {isOpen && (
				<div className={styles.menu} ref={menuRef}>
					{isGrouped
					  ? options.map((group) => (
						<div key={group.label} className={styles.group}>
							<div className={styles.groupHeading}>{group.label}</div>
							{group.options.map((option) => (
							  <div
								key={option.value}
								className={styles.option}
								onClick={() => handleOptionClick(option)}
								onKeyDown={(e) => e.key === 'Enter' && handleOptionClick(option)}
								role="option"
								aria-selected={selectedValue && selectedValue.value === option.value}
								tabIndex={0}
								title={option.label} // Add title for full text on hover
							  >
								  {option.label}
							  </div>
							))}
						</div>
					  ))
					  : options.map((option) => (
						<div
						  key={option.value}
						  className={styles.option}
						  onClick={() => handleOptionClick(option)}
						  onKeyDown={(e) => e.key === 'Enter' && handleOptionClick(option)}
						  role="option"
						  aria-selected={selectedValue && selectedValue.value === option.value}
						  tabIndex={0}
						  title={option.label} // Add title for full text on hover
						>
							{option.label}
						</div>
					  ))}
				</div>
			  )}
		  </label>
		  {error && <div className={styles.error} dangerouslySetInnerHTML={{__html: error}}/>}
		  {selectedValue && <input type="hidden" name={name} value={selectedValue.value}/>}
	  </div>
	);
};

Select.propTypes = {
	name: PropTypes.string.isRequired,
	options: PropTypes.array.isRequired,
	onChange: PropTypes.func,
	selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	placeholder: PropTypes.string,
	isDisabled: PropTypes.bool,
	isGrouped: PropTypes.bool,
	error: PropTypes.string,
	id: PropTypes.string,
	layout: PropTypes.oneOf(['Default']),
	color: PropTypes.string
};

export default Select;